import React, { useEffect, useState } from 'react';
import './UserActionsPanel.css';

/**
 * UserActionsPanel Component
 * 
 * Provides interactive controls for radiologists to interact with AI suggestions.
 * 
 * @param {Object} props - Component props
 * @param {string} props.patientId - Patient identifier
 * @param {Object} props.diagnosticData - Current diagnostic data
 * @param {Function} props.onAccept - Accept action callback
 * @param {Function} props.onReject - Reject action callback
 * @param {Function} props.onEdit - Edit action callback
 * @param {Function} props.onExplain - Explain action callback
 */
const UserActionsPanel = ({
  patientId,
  diagnosticData,
  findingsData,
  messagesTemplate,
  imageInfo,
  onAccept,
  onReject,
  onEdit,
  onExplain
}) => {
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [acceptedBirads, setAcceptedBirads] = useState('');
  const [editedLabel, setEditedLabel] = useState('');
  const [editedFindings, setEditedFindings] = useState('');
  const [actionStatus, setActionStatus] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!actionStatus) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setActionStatus(null);
    }, 3500);

    return () => clearTimeout(timeoutId);
  }, [actionStatus]);

  const showSuccessStatus = (message, actionType = 'success') => {
    setActionStatus({ type: actionType, message });
  };

  const loadDiagnosticText = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/assistant/outputs/${patientId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch study: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('Diagnostic text successfully fetched from backend: ', data.patient?.[0]?.diagnosticText);
      return data.patient?.[0]?.diagnosticTextPhys || data.patient?.[0]?.diagnosticTextAssis || '';
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error fetching diagnostic text:', errorMsg);
    }
  };

  return (
    <div className="user-actions-panel">
      <div className="panel-header">
        <h4>Actions</h4>
      </div>

      <div className="panel-content">
        {actionStatus && (
          <div className={`action-feedback action-${actionStatus.type}`} role="status" aria-live="polite">
            {actionStatus.message}
          </div>
        )}

        {/* Action Buttons */}

        <button
          className="action-btn accept"
          onClick={() => setShowAcceptModal(true)}
          disabled={!diagnosticData}
        >
          <span className="btn-text">Accept Suggestion</span>
        </button>

        <button
          className="action-btn reject"
          onClick={() => setShowRejectModal(true)}
          disabled={!diagnosticData}
        >
          <span className="btn-text">Reject Suggestion</span>
        </button>

        <button
          className="action-btn edit"
          onClick={async () => {
            setShowEditModal(true);
            const patientInfo = diagnosticData?.patient?.[0];
            if (patientInfo) {
              const diagnosticText = patientInfo.diagnosticTextPhys || patientInfo.diagnosticTextAssis || await loadDiagnosticText();
              setEditedLabel(patientInfo.biradsLabelPhys || patientInfo.biradsLabelAssis || '');
              // Generate clinical findings text if available
              setEditedFindings(diagnosticText);
            }
          }}
          disabled={!diagnosticData}
        >
          <span className="btn-text">Edit Suggestion</span>
        </button>

        {/* Explanation Section */}
        {/*
        {!showExplanation ? (
          <button
            className="action-btn explain"
            onClick={() => {
              setShowExplanation(true);
              if (onExplain) {
                onExplain();
              }
            }}
            disabled={!diagnosticData}
          >
            <span className="btn-text">Request Explanation</span>
          </button>
        ) : (
          <div className="explanation-container">
            <div className="explanation-header">
              <h5>AI Explanation</h5>
              <button
                className="close-explanation-btn"
                onClick={() => setShowExplanation(false)}
                title="Close explanation"
              >
                ×
              </button>
            </div>
            <div className="explanation-content">
              <p>
                PLACEHOLDER: The AI model assessed this case based on detected features including mass characteristics,
                calcification patterns, and tissue density. The BI-RADS classification was determined by
                analyzing morphological features and comparing them with known diagnostic patterns.
                High-risk features such as irregular margins, heterogeneous density, and suspicious
                calcification distributions contributed to the overall assessment.
              </p>
              <p className="explanation-note">
                <strong>Note:</strong> This is a placeholder explanation.
              </p>
            </div>
          </div>
        )}
        */}
      </div>

      {/* Accept Modal */}
      {showAcceptModal && (
        <div className="modal-overlay" onClick={() => setShowAcceptModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Accept Suggestion</h3>
              <button
                className="close-btn"
                onClick={() => setShowAcceptModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="edit-field">
                <label htmlFor="birads-assessment">BI-RADS Assessment:</label>
                <select
                  id="birads-assessment"
                  className="birads-select"
                  value={acceptedBirads}
                  onChange={(e) => setAcceptedBirads(e.target.value)}
                >
                  <option value="">Select BI-RADS</option>
                  <option value="0">0 - Incomplete</option>
                  <option value="1">1 - Negative</option>
                  <option value="2">2 - Benign</option>
                  <option value="3">3 - Probably Benign</option>
                  <option value="4">4 - Suspicious</option>
                  <option value="5">5 - Highly Suggestive of Malignancy</option>
                  <option value="6">6 - Known Biopsy-Proven Malignancy</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowAcceptModal(false);
                }}
              >
                Cancel
              </button>
              <button
                className="confirm-accept-btn"
                onClick={() => {
                  if (onAccept) {
                    onAccept();
                  }
                  showSuccessStatus('Suggestion accepted.', 'accept');
                  setShowAcceptModal(false);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Suggestion</h3>
              <button
                className="close-btn"
                onClick={() => setShowRejectModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div>
                <label htmlFor="rejection-reason">Reason for rejection:</label>
                <textarea
                  id="rejection-reason"
                  className="modal-textarea"
                  placeholder="Please provide a reason for rejecting this suggestion..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={6}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
              >
                Cancel
              </button>
              <button
                className="confirm-reject-btn"
                onClick={() => {
                  if (onReject) {
                    onReject(rejectionReason);
                  }
                  console.log('Rejection reason:', rejectionReason);
                  showSuccessStatus('Suggestion rejected.', 'reject');
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Suggestion</h3>
              <button
                className="close-btn"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="edit-field">
                <label htmlFor="birads-label">Suspicion Level:</label>
                <select
                  id="birads-label"
                  className="birads-select"
                  value={editedLabel}
                  onChange={(e) => setEditedLabel(e.target.value)}
                >
                  <option value="">Select label</option>
                  <option value="Low">Low</option>
                  <option value="Moderate">Moderate</option>
                  <option value="High">High</option>
                </select>
              </div>
              <div className="edit-field">
                <label htmlFor="clinical-findings">Clinical Findings:</label>
                <textarea
                  id="clinical-findings"
                  className="findings-textarea"
                  placeholder="Enter clinical findings..."
                  value={editedFindings}
                  onChange={(e) => setEditedFindings(e.target.value)}
                  rows={8}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowEditModal(false);
                  setEditedLabel('');
                  setEditedFindings('');
                }}
              >
                Cancel
              </button>
              <button
                className="confirm-edit-btn"
                onClick={() => {
                  if (onEdit) {
                    onEdit({
                      label: editedLabel,
                      findings: editedFindings,
                    });
                  }
                  showSuccessStatus('Suggestion updated.', 'edit');
                  setShowEditModal(false);
                  setEditedLabel('');
                  setEditedFindings('');
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserActionsPanel;
